// src/pages/materias/laboral/CrearExpedientePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";

import FormCentroConciliacion from "../../components/forms/centro_conciliacion/FormCentroConciliacion";
import {actionConciliacionGetByID} from "../../redux/actions/conciliacion/conciliacion";
import { notify } from "../../components/ui";

// ====== helpers ======
const pad2 = (n) => String(n).padStart(2, "0");

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const isoToDDMMYYYY_HHMM = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
};

const isoToDayjs = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return dayjs(iso);
};

const extractFolioFromExpediente = (expediente) => {
  if (!expediente) return "";
  const parts = String(expediente).split("/");
  return parts.length ? parts[parts.length - 1] : String(expediente);
};

const backendToInitialValues = (d) => {
  if (!d) return {};

  // ✅ RS como ARRAY DE IDS (Select NO usa labelInValue)
  const rs = Array.isArray(d.razones_sociales) ? d.razones_sociales : [];
  const rsIds = rs
    .map((x) => x?.id_razon_social ?? x?.id ?? null)
    .filter((v) => v !== null && v !== undefined)
    .map((v) => Number(v));

  const formaNot = d.tipo_notificado === "actuario" ? "actuario" : "trabajador";
  const proximaAudISO = d.fecha_proxima_audiencia || d.fecha_audiencia_inicial || null;

  return {
    exp: d.expediente,

    empresa_razon_social_id: rsIds,

    trabajadores: Array.isArray(d.trabajadores) && d.trabajadores.length
      ? d.trabajadores.map((t) => t.nombre_trabajador)
      : [""],
    abogado_contrario: d.abogado_contrario ?? "",
    objeto_solicitud: d.nombre_objeto ?? "",
    forma_notificacion: formaNot,
    origen_actuario: d.tipo_notificado_actuario ?? undefined,

    id_medio_notificacion:
      d.id_medio_notificacion != null ? Number(d.id_medio_notificacion) : undefined,

    fecha_emision_expediente: isoToDDMMYYYY(d.fecha_emision_expediente),
    fecha_audiencia: proximaAudISO ? isoToDDMMYYYY_HHMM(proximaAudISO) : "",

    fecha_proxima_audiencia: isoToDayjs(proximaAudISO),

    fecha_hora_cita_recepcion: d.fecha_notificacion
      ? isoToDDMMYYYY_HHMM(d.fecha_notificacion)
      : "",
  };
};

const getInt = (sp, key) => {
  const v = sp.get(key);
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
export default function CrearExpedientePage(props) {
  const location = useLocation();
  const [searchParams] = useSearchParams();


const idEstado = getInt(searchParams, "estado") ?? location.state?.idEstado ?? null;
const idCiudad = getInt(searchParams, "ciudad") ?? location.state?.idCiudad ?? null;
const idAutoridad = getInt(searchParams, "autoridad") ?? location.state?.idAutoridad ?? null;

  const nombreIdentificacionCiudad =
    location.state?.nombreIdentificacionCiudad ?? "";

  const fromDesvinculacion = location.state?.fromDesvinculacion ?? null;
  const prefillData = location.state?.prefill ?? null;



  const { idExpediente } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEdit = Boolean(idExpediente);

  const { detalle } = useSelector((state) => state?.conciliacion ?? {});
  const [loadingEdit, setLoadingEdit] = useState(false);

  // KEY estable: evita recalcular initialValues por “cualquier” cambio del objeto detalle
  const detalleKey = useMemo(() => {
    if (!isEdit) return "";
    return String(detalle?.updated_at || detalle?.id || idExpediente || "");
  }, [isEdit, detalle?.updated_at, detalle?.id, idExpediente]);

  // initialValues estable: en CREATE usa prefill si viene de desvinculación
  const emptyInitialValues = useMemo(() => {
    if (!prefillData) return {};
    const init = { trabajadores: [prefillData.trabajador_nombre || ""] };
    if (prefillData.id_razon_social) {
      init.empresa_razon_social_id = [Number(prefillData.id_razon_social)];
    }
    return init;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues = useMemo(() => {
    if (!isEdit) return emptyInitialValues;
    return backendToInitialValues(detalle);
  }, [isEdit, detalleKey, emptyInitialValues]);



  const handleBack = () => {
    if (isEdit && location.state?.from === "detail") {
      navigate(`/materias/laboral/centro-conciliacion/${idExpediente}`);
    } else {
      const backSearch = location.state?.backSearch || "";
      navigate(`/materias/laboral/centro-conciliacion${backSearch}`);
    }
  };

  // Cuando viene de una desvinculación: navegar de vuelta con el ID creado
  const handleCreated = fromDesvinculacion
    ? (expedienteId) => {
        navigate(`/materias/laboral/desvinculaciones/${fromDesvinculacion}`, {
          state: { linkedConciliacionId: expedienteId },
        });
      }
    : undefined;

  useEffect(() => {
    if (!isEdit) return;

    (async () => {
      try {
        setLoadingEdit(true);

        await dispatch(actionConciliacionGetByID(idExpediente));
       
      } catch (e) {
        notify.error("No se pudo cargar el expediente", e?.message || "Sin detalle");
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [dispatch, isEdit, idExpediente]);

  const commonProps = {
    // Cuando viene de desvinculación, onCreated maneja la navegación; onSaved no debe navegar
    onSaved: fromDesvinculacion ? () => {} : () => handleBack(),
    onCancel: () => handleBack(),
  };

  const formNode = (
    <FormCentroConciliacion
      {...commonProps}
      idEstado={idEstado}
      isEdit={isEdit}
      idCiudad={idCiudad}
      idExpediente={idExpediente}
      initialValues={initialValues}
      idAutoridad={idAutoridad}
      nombreIdentificacionCiudad={nombreIdentificacionCiudad}
      showTopActions={true}
      onCreated={handleCreated}
    />
  );

  return (
    <main className="laboral-main">
      <div className="expediente-page-container">
        {isEdit && loadingEdit ? (
          <div className="d-flex align-items-center gap-2 text-secondary py-5 justify-content-center">
            <Spin size="small" /> Cargando expediente...
          </div>
        ) : (
          formNode
        )}
      </div>

      <style>
        {`
.expediente-page-container {
  width: 100%;
  margin: 0 auto;
}

.laboral-header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.laboral-header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.laboral-back-btn {
  padding-left: 0;
}
        `}
      </style>
    </main>
  );
}
